import keycloak from '../keycloak';
import { redirectToLogin } from './redirectToLogin';

const API_URL = process.env.REACT_APP_API_URL;

// Check if we're in print mode (for PDF generation)
const urlParams = new URLSearchParams(window.location.search);
const isPrintMode = urlParams.get('print') === 'true';
const injectedToken = urlParams.get('token');

/**
 * Creates an authenticated fetch function that automatically handles token refresh
 * on 401/403 errors. This is the central place for all API authentication logic.
 * 
 * @param {string} url - Full URL or endpoint path
 * @param {Object} options - Fetch options (method, body, headers, etc.)
 * @param {boolean} retryWithRefresh - Internal flag to prevent infinite retry loops
 * @returns {Promise<Response>}
 */
export const authenticatedFetch = async (url, options = {}, retryWithRefresh = false) => {
  // In print mode, use the injected token if keycloak token isn't available
  const token = keycloak?.token || (isPrintMode ? injectedToken : null);
  
  if (!token) {
    throw new Error('Authentication required');
  }

  // Helper to refresh token and retry
  const fetchWithTokenRefresh = async (isRetry = false) => {
    // Try to refresh token if this is a retry after 401/403
    // In print mode, we can't refresh tokens, so just use the injected token
    let currentToken = token;
    if (isRetry && !isPrintMode && keycloak?.updateToken) {
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          currentToken = keycloak.token;
        }
      } catch (refreshError) {
        console.error('Token refresh failed, redirecting to login:', refreshError);
        redirectToLogin();
        throw new Error('Authentication failed: token refresh failed');
      }
    }

    // Build headers
    const headers = new Headers(options.headers || {});
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', `Bearer ${currentToken}`);

    // Build full URL if endpoint is provided
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;

    // Build request options
    const requestOptions = {
      ...options,
      headers,
    };

    // Stringify body if it's an object
    if (requestOptions.body && typeof requestOptions.body === 'object') {
      requestOptions.body = JSON.stringify(requestOptions.body);
    }

    // Debug logging for print mode
    if (isPrintMode) {
      console.log('authenticatedFetch: ' + JSON.stringify({
        fullUrl,
        method: requestOptions.method || 'GET',
        hasToken: !!currentToken,
        tokenLength: currentToken?.length
      }));
    }

    let response;
    try {
      // In print mode, add a 15-second timeout using AbortController
      if (isPrintMode) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.error('authenticatedFetch TIMEOUT after 15s: ' + fullUrl);
          controller.abort();
        }, 15000);
        
        try {
          response = await fetch(fullUrl, { ...requestOptions, signal: controller.signal });
        } finally {
          clearTimeout(timeoutId);
        }
      } else {
        response = await fetch(fullUrl, requestOptions);
      }
    } catch (fetchError) {
      // Log fetch errors in print mode
      if (isPrintMode) {
        console.error('authenticatedFetch FAILED: ' + JSON.stringify({
          url: fullUrl,
          error: fetchError.message,
          errorType: fetchError.name
        }));
      }
      throw fetchError;
    }
    
    // Log response in print mode
    if (isPrintMode) {
      // Get content-length header to check response size
      const contentLength = response.headers.get('content-length');
      console.log('authenticatedFetch response headers: ' + JSON.stringify({
        url: fullUrl,
        status: response.status,
        ok: response.ok,
        contentLength: contentLength || 'unknown'
      }));
    }

    // Handle 401/403 errors by attempting token refresh and retry
    // In print mode, we can't refresh tokens, so just fail
    if ((response.status === 401 || response.status === 403) && !isRetry && !isPrintMode && keycloak?.updateToken) {
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          // Retry the request with the new token
          return await fetchWithTokenRefresh(true);
        } else {
          // Token refresh didn't happen, but we still got 401/403
          // This could be a permissions issue (403) or invalid token (401)
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (refreshError) {
        console.error('Token refresh failed, redirecting to login:', refreshError);
        redirectToLogin();
        throw new Error('Authentication failed: token refresh failed');
      }
    }

    if (!response.ok) {
      let serverBody = null;
      try {
        const text = await response.text();
        if (text) serverBody = JSON.parse(text);
      } catch (_) {
        // ignore parse error
      }
      const err = new Error(serverBody?.error?.message || serverBody?.message || `HTTP error! status: ${response.status}`);
      err.status = response.status;
      err.serverBody = serverBody;
      throw err;
    }

    return response;
  };

  return fetchWithTokenRefresh(retryWithRefresh);
};

/**
 * Convenience method for authenticated GET requests
 */
export const authenticatedGet = async (endpoint, params = {}) => {
  let url = endpoint;
  if (Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  const response = await authenticatedFetch(url, { method: 'GET' });
  return response.json();
};

/**
 * Convenience method for authenticated POST requests
 */
export const authenticatedPost = async (endpoint, body = {}, params = {}) => {
  let url = endpoint;
  if (Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  const response = await authenticatedFetch(url, {
    method: 'POST',
    body,
  });
  return response.json();
};

/**
 * Convenience method for authenticated PUT requests
 */
export const authenticatedPut = async (endpoint, body = {}, params = {}) => {
  let url = endpoint;
  if (Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  const response = await authenticatedFetch(url, {
    method: 'PUT',
    body,
  });
  return response.json();
};

/**
 * Convenience method for authenticated PATCH requests
 */
export const authenticatedPatch = async (endpoint, body = {}, params = {}) => {
  let url = endpoint;
  if (Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  const response = await authenticatedFetch(url, {
    method: 'PATCH',
    body,
  });
  return response.json();
};

/**
 * Convenience method for authenticated DELETE requests
 */
export const authenticatedDelete = async (endpoint, params = {}) => {
  let url = endpoint;
  if (Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }
  const response = await authenticatedFetch(url, { method: 'DELETE' });
  // A 204 No Content (the standard REST response for DELETE) has no body — calling
  // response.json() on it throws "Unexpected end of JSON input". Read as text first
  // and only parse when there's actually a body, so both 204-empty and JSON-body
  // DELETE responses are handled.
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

